// Client-side account-group filtering for the Automation Reports tab
// (2026-10-07, Patrick's request for 1A eBay/AM eBay/1A Website/AM Website/
// Support Email filters). Each flow's rate/failure numbers already arrive
// broken out per account group per week (`byGroupWeek`, see
// getAutomationRateTrend/getAutomationFailureTrend's own module comment in
// repositories/reporting.js), so narrowing the filter is pure client-side
// summation -- no re-fetch, same "pool client-side, react instantly"
// pattern ui/src/ahtByRepPooling.js and ui/src/scorecardsPooling.js already
// use for their own filters.

function sumGroups(byGroupWeek, groups, week) {
  return groups.reduce((sum, g) => sum + (byGroupWeek[g]?.[week] ?? 0), 0)
}

// rateData: the raw `rate` object from GET /automation-trends
// (weekLabels, flows[{flowKey, displayName, byGroupWeek}], totalValidByGroupWeek).
// selectedGroups: which of AUTOMATION_TREND_GROUPS are currently checked.
//
// Pools each flow's automated COUNT across the selected groups, then divides
// by that SAME selection's pooled total-valid count -- never an average of
// per-group rates, which would misweight a low-volume group the same as a
// high-volume one (same sum-first-divide-once rule this whole page follows).
// A flow with zero automated resolutions across the selected groups is
// dropped from the result (even if it had some in a group that's now
// unchecked) -- same "omit rather than show an all-zero row" rule the
// server-side report already follows for the unfiltered case.
export function poolAutomationRate(rateData, selectedGroups) {
  if (!rateData?.weekLabels) return rateData
  const { weekLabels, flows, totalValidByGroupWeek } = rateData

  const totalValidPoints = {}
  for (const week of weekLabels) totalValidPoints[week] = sumGroups(totalValidByGroupWeek ?? {}, selectedGroups, week)

  const pooledFlows = flows
    .map((f) => {
      const counts = {}
      const points = {}
      for (const week of weekLabels) {
        counts[week] = sumGroups(f.byGroupWeek ?? {}, selectedGroups, week)
        points[week] = totalValidPoints[week] > 0 ? counts[week] / totalValidPoints[week] : 0
      }
      return { flowKey: f.flowKey, displayName: f.displayName, counts, points }
    })
    .filter((f) => weekLabels.some((week) => f.counts[week] > 0))

  const totalAutomatedPoints = {}
  for (const week of weekLabels) totalAutomatedPoints[week] = pooledFlows.reduce((sum, f) => sum + f.counts[week], 0)

  return { weekLabels, flows: pooledFlows, totalAutomatedPoints, totalValidPoints }
}

// Same pooling rule as poolAutomationRate above, for the failures table --
// just a plain count per flow per week, no denominator.
export function poolAutomationFailures(failureData, selectedGroups) {
  if (!failureData?.weekLabels) return failureData
  const { weekLabels, flows } = failureData

  const pooledFlows = flows
    .map((f) => {
      const counts = {}
      for (const week of weekLabels) counts[week] = sumGroups(f.byGroupWeek ?? {}, selectedGroups, week)
      return { flowKey: f.flowKey, displayName: f.displayName, counts }
    })
    .filter((f) => weekLabels.some((week) => f.counts[week] > 0))

  const totalCounts = {}
  for (const week of weekLabels) totalCounts[week] = pooledFlows.reduce((sum, f) => sum + f.counts[week], 0)

  return { weekLabels, flows: pooledFlows, totalCounts }
}
